import React, { useState } from 'react';
import type { Clave } from '../../../i18n/textos';
import { useIdioma, type Traducir } from '../../../i18n/idioma';
import { nombreDeDato } from '../../../i18n/nombresDeDatos';
import toast from 'react-hot-toast';
import {
  FileText, ArrowRight, ShieldAlert, Users, Calculator, Plus,
  ChevronDown, ChevronUp, ExternalLink, AlertTriangle, Info,
  Construction
} from 'lucide-react';
import { CategoryDef } from '../../../data/categories/registry';
import { loadEstimateTemplates, loadCategoryBundle } from '../../../data/categories/loader';
import { hintResolves } from '../../../data/categories/catalogLoader';
import type { EstimateTemplate, TemplateLineItem } from '../../../data/categories/estimateTemplateTypes';
import type { EstimateItem, EstimateItemType, EstimateZone } from '../../../types';
import { getTemplateWarnings, highestSeverity, type TemplateWarning } from '../../../data/categories/templateWarnings';
import { countUnpricedItems, getFirstUnpricedItem, isUnpricedItem } from '../../../utils/estimatePricingGaps';
import { applyRegionalCost } from '../../../utils/regionalCostMultiplier';
import PricingNextStepCard from '../PricingNextStepCard';
import PricingNextStepModal from '../PricingNextStepModal';
import { Button } from '../../ui/Button';
import { EmptyState } from '../../ui/EmptyState';
import { StatusDot, StatusTone } from '../../ui/StatusDot';
import { priceLineByType } from '../../../utils/lineItemPricing';

interface LaborOverheadInputs {
  baseLaborCost: number;
  laborBurdenPercent: number;
  overheadPerManHour: number;
  breakEvenRate?: number;
}

interface CategoryTemplatesViewProps {
  /** Trade id matching `data/categories/loader.ts` CATEGORY_FOLDER_BY_ID.
   *  Required — without it, template loading is hard-coded and breaks every
   *  trade except landscaping. */
  tradeId: string;
  category: CategoryDef;
  /** Workspace target margin (percent 0..99). Drives labor/other line pricing on
   *  dropped rows (rate = cost / (1 − margin)). */
  targetMarginPercent: number;
  /** Workspace default material markup (percent). Drives MATERIAL line pricing on
   *  dropped rows (rate = cost × (1 + markup)). */
  materialMarkupPercent: number;
  /** Workspace pricebook items (id-keyed by name lowercased — see resolver). */
  pricebookByHint: Map<string, number>;
  /** Workspace overhead settings — when a labor line has no pricebook/role
   *  match, we fall back to (baseLaborCost × (1 + burden/100)) + overhead so
   *  labor never drops in at $0 even on $0-priced templates. */
  laborOverhead: LaborOverheadInputs;
  /** Existing zones in the estimate. Surfaced as a per-template-card dropdown
   *  override — the default is to auto-create a new zone named after the
   *  template, but the user can target an existing zone here if they want to
   *  merge templates into one logical group like "Backyard". */
  zones: EstimateZone[];
  /** Drops a template's items into the estimate. The implementation auto-
   *  creates a zone named after the template when no `targetZoneId` is given,
   *  and stamps it with `sourceTemplateId` so the Save flow can prompt the
   *  contractor to save it as a private template. */
  onAddTemplateItems: (items: EstimateItem[], template: { id: string; name: string }, targetZoneId?: string) => void;
  /** Current estimate line items — used to block stacking templates before pricing. */
  existingItems?: EstimateItem[];
  /** US state code for light regional adjustment on template fallbacks. */
  businessState?: string;
  /** Scroll the document to rows that still need a cost. */
  onFocusUnpriced?: () => void;
  /** Optional: jump to the Calculators tab focused on this category. */
  onOpenCalculator?: () => void;
}

/** The fallback labor cost we apply when neither pricebook nor role rate
 *  resolves AND the template default is 0. Mirrors EstimateToolPanel's
 *  handleAddLabor formula so manually-added labor and template-derived labor
 *  use the same rate basis. */
function computeOverheadLaborCost(s: LaborOverheadInputs): number {
  if (s.breakEvenRate && s.breakEvenRate > 0) return s.breakEvenRate;
  const burdened = (s.baseLaborCost || 0) * (1 + (s.laborBurdenPercent || 0) / 100);
  return burdened + (s.overheadPerManHour || 0);
}

/* La llave (`sourced`, `triangulated`…) viene del dato de la plantilla; solo la
   etiqueta es texto, así que va como clave. */
const CONFIDENCE_PILL: Record<string, { tone: StatusTone; label: Clave }> = {
  sourced:      { tone: 'exito',  label: 'estimado.confianza.sourced' },
  triangulated: { tone: 'info',   label: 'estimado.confianza.triangulated' },
  estimated:    { tone: 'aviso',  label: 'estimado.confianza.estimated' },
};

/** Micro-etiqueta neutra del sistema — reemplaza los chips pastel de 9px. */
const MetaTag: React.FC<{ icon?: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <span className="label-ui inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg bg-acero-100">
    {icon}{children}
  </span>
);

/** Map a v2 line-item type to the EstimateItem type used by the builder. */
function mapLineType(tipo: TemplateLineItem['type']): EstimateItemType {
  if (tipo === 'service_block') return 'Material';
  return (tipo.charAt(0).toUpperCase() + tipo.slice(1)) as EstimateItemType;
}

function getSeedCostFromBundle(
  tradeId: string,
  categoryId: string,
  hint: string,
  stateCode?: string
): number | null {
  const bundle = loadCategoryBundle(tradeId, categoryId);
  const seed = bundle?.bundle?.pricebookSeed?.find(s => s.pricebookItemHint === hint);
  if (!seed) return null;
  const base =
    seed.unitPriceUsdRecommendedDefault ??
    ((seed.unitPriceUsdLow + seed.unitPriceUsdHigh) / 2);
  if (base <= 0) return null;

  if (stateCode && seed.regionAdjusters?.length) {
    const stateUpper = stateCode.toUpperCase();
    const match = seed.regionAdjusters.find(
      r =>
        r.region.toUpperCase() === stateUpper ||
        r.region.toUpperCase().includes(stateUpper)
    );
    if (match) {
      return Math.round(base * ((match.multiplierLow + match.multiplierHigh) / 2) * 100) / 100;
    }
  }
  return applyRegionalCost(base, stateCode);
}

/** Resuelve el costo base de una partida, por orden de prioridad:
 *    1. Pricebook del taller (via pricebookItemHint, materiales y equipo)
 *    2. Costo de mano de obra derivado del overhead (partidas de labor)
 *    3. Semilla del bundle de categoría (default nacional, ajustado por región)
 *    4. El unitPriceUsd de la plantilla (ajustado por región cuando es > 0)
 *
 * Aquí vivía un nivel 2 de "tarifa por puesto" que ya no existe. Estaba muerto
 * por partida doble: la migración 020 reescribió las llaves guardadas a
 * `landscaping.<slug>` mientras que los 47 roles del corpus son slugs pelados,
 * así que la búsqueda no podía acertar nunca; y 1.889 de las 2.135 líneas de
 * labor no traen `role` siquiera.
 */
function resolveCost(
  li: EstimateTemplate['lineItems'][number],
  pricebookByHint: Map<string, number>,
  laborOverhead: LaborOverheadInputs,
  tradeId: string,
  categoryId: string,
  businessState?: string,
): { cost: number; source: 'pricebook' | 'overhead-derived' | 'template-default' } {
  // MANO DE OBRA: un solo costo mezclado por HORA-HOMBRE, punto. Ni pricebook,
  // ni catálogo, ni valor por defecto de la plantilla — la mano de obra nunca
  // es una línea de catálogo. `cost` sale en $/hora-hombre y quien llama lo
  // multiplica por manHours, NO por qty: qty describe lo que ve el cliente
  // ("1 crew-day") y cobrarlo como una hora es el error de 10-25x que esto
  // arregla.
  if (li.type === 'labor') {
    return { cost: computeOverheadLaborCost(laborOverhead), source: 'overhead-derived' };
  }
  if (li.pricebookItemHint && pricebookByHint.has(li.pricebookItemHint)) {
    return { cost: pricebookByHint.get(li.pricebookItemHint)!, source: 'pricebook' };
  }
  if (li.pricebookItemHint) {
    const seedCost = getSeedCostFromBundle(tradeId, categoryId, li.pricebookItemHint, businessState);
    if (seedCost && seedCost > 0) {
      return { cost: seedCost, source: 'template-default' };
    }
  }
  if (li.unitPriceUsd > 0) {
    return { cost: applyRegionalCost(li.unitPriceUsd, businessState), source: 'template-default' };
  }
  return { cost: li.unitPriceUsd, source: 'template-default' };
}

/** Convert one template's lineItems into EstimateItems via the priority resolver.
 *  Materials price on the workspace MATERIAL MARKUP dial (rate = cost × (1+markup));
 *  labor and every other line type price on the TARGET MARGIN dial. */
function lineItemsToEstimateItems(
  template: EstimateTemplate,
  targetMarginPercent: number,
  materialMarkupPercent: number,
  pricebookByHint: Map<string, number>,
  laborOverhead: LaborOverheadInputs,
  tradeId: string,
  categoryId: string,
  businessState?: string,
  /** Suelta, no componente: `t` entra por parámetro. */
  t?: Traducir,
): EstimateItem[] {
  return template.lineItems.map(li => {
    const { cost } = resolveCost(li, pricebookByHint, laborOverhead, tradeId, categoryId, businessState);
    const itemType = mapLineType(li.type);

    // La cantidad de la que sale el dinero de la fila. Para mano de obra son
    // HORAS-HOMBRE (`cost` viene en $/hora-hombre); el qty/unit de la plantilla
    // describe el entregable que ve el cliente ("1 crew-day") y cobrarlo tal
    // cual facturaría el día de una cuadrilla de 3 como si fuera una hora.
    //
    // El respaldo a li.qty existe solo para que una plantilla anterior al
    // Esquema v4 degrade al comportamiento viejo —malo, pero no cero— en vez
    // de entrar a $0. templateManHours.test.ts es lo que mantiene ese respaldo
    // inalcanzable para el corpus.
    const isLabor = li.type === 'labor';
    const billableQty = isLabor ? (li.manHours ?? li.qty) : li.qty;
    // Con la cantidad ya en horas-hombre, la fila queda denominada en horas.
    // 'HR' es lo que espera unitLabel() y todo consumidor de horas río abajo.
    const rowUnit = isLabor ? 'HR' : li.unit;

    // Una sola regla por tipo, en utils/lineItemPricing. Antes el material solo
    // tomaba el dial de markup si era > 0, y con el dial en 0 caía al `else` de
    // margen: el mismo taller obtenía $28,57 por un kit de $20 al soltar la
    // plantilla y $20,00 exactos al mover `applyMaterialMarkup(0)`. Dos precios
    // para una sola configuración. Un dial en 0 significa "no marcar", y ahora
    // eso es lo que hace.
    const { rate, markupPercent, marginPercent } = priceLineByType(itemType, cost, {
      targetMarginPercent,
      materialMarkupPercent,
    });
    // Validator: surface unresolved hints in the row description so contractors
    // see when a template references a slug that's not in the catalog.
    // Material/equipment lines only — labor uses `role` not `pricebookItemHint`.
    const needsCatalog = li.type === 'material' || li.type === 'equipment';
    const isUnresolved = needsCatalog && li.pricebookItemHint && !hintResolves(li.pricebookItemHint);
    const description = isUnresolved
      ? `${li.description} ${t ? t('estimado.plantillas.sinCatalogo') : '⚠'} ${li.pricebookItemHint}`
      : li.description;
    return {
      id: Math.random().toString(36).substr(2, 9),
      type: itemType,
      description,
      quantity: billableQty,
      cost,
      markupPercent,
      marginPercent,
      rate,
      // El precio que produjo la REGLA de la fila ES su precio base: nace con
      // k = 1, "exactamente los diales del taller". Se estampa aquí porque sin
      // él el solver de margen neto tendría que adivinarlo a partir del `rate`
      // ya escalado, y una plantilla soltada DESPUÉS de mover el dial quedaría
      // una generación por detrás de una idéntica soltada antes (Parte A.6.6).
      baseRate: rate,
      total: rate * billableQty,
      unit: rowUnit,
      // La medida del cliente, solo cuando difiere de la que se cobra.
      clientQty: isLabor ? li.qty : undefined,
      clientUnit: isLabor ? li.unit : undefined,
      // Sello de cómo salió la fila, para que el diff de guardado no compare
      // contra un archivo que declara todos los precios en 0.
      dropQty: billableQty,
      dropCost: cost,
      sourcePricebookId: li.pricebookItemHint,
      isOverridden: false,
    } as EstimateItem;
  });
}

/** Look up calculator template ids that exist for this category, used to
 *  decide whether `hasCalculatorVariant: true` resolves to a real bundle or
 *  is still pending. */
function loadCalculatorIds(trade: string, categoryId: string): Set<string> {
  const bundle = loadCategoryBundle(trade, categoryId);
  if (!bundle) return new Set();
  return new Set(bundle.templates.map(plantilla => plantilla.id));
}

const CategoryTemplatesView: React.FC<CategoryTemplatesViewProps> = ({
  tradeId, category, targetMarginPercent, materialMarkupPercent, pricebookByHint, laborOverhead, zones,
  onAddTemplateItems, existingItems = [], businessState, onFocusUnpriced, onOpenCalculator,
}) => {
    const { t } = useIdioma();
  const Icon = category.icon;
  const templates = loadEstimateTemplates(tradeId, category.id);
  const calculatorIds = loadCalculatorIds(tradeId, category.id);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Per-card destination zone override. Empty string = auto-create a fresh
  // zone named after the template (the default and recommended path). Any
  // other value = drop into that existing zone instead.
  const [zoneByTemplateId, setZoneByTemplateId] = useState<Record<string, string>>({});
  const [pendingTemplate, setPendingTemplate] = useState<EstimateTemplate | null>(null);
  const unpricedExisting = countUnpricedItems(existingItems);
  const nextUnpriced = getFirstUnpricedItem(existingItems);

  // Empty state — no templates JSON for this category yet.
  if (!templates || templates.length === 0) {
    return (
      <div className="px-4 py-2">
        <EmptyState
          icon={<Icon size={18} />}
          title={`${category.label} ${t('estimado.plantillas.vacio.titulo')}`}
          description={category.note || t('estimado.plantillas.sinPlantillas')}
          action={onOpenCalculator ? (
            <Button variant="secondary" onClick={onOpenCalculator} rightIcon={<ArrowRight size={14} />}>
              {t('estimado.plantillas.vacio.boton')}
            </Button>
          ) : undefined}
        />
        <div className="mx-auto w-full max-w-sm bg-acero-50 border border-acero-200 rounded-xl p-4 flex items-start gap-3 text-left">
          <FileText size={16} className="text-acero-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="label-ui">{t('estimado.plantillas.ayuda.titulo')}</p>
            <p className="text-[11px] text-acero-500 mt-1 leading-relaxed">
              {t('estimado.plantillas.ayuda.texto1')} <code className="font-data">documents/categories/landscaping/{category.id}/templates/</code> {t('estimado.plantillas.ayuda.texto2')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const executeAdd = (plantilla: EstimateTemplate) => {
    const items = lineItemsToEstimateItems(
      plantilla, targetMarginPercent, materialMarkupPercent, pricebookByHint, laborOverhead,
      tradeId, category.id, businessState, t
    );
    const targetZoneId = zoneByTemplateId[plantilla.id] || undefined;
    onAddTemplateItems(items, { id: plantilla.id, name: plantilla.name }, targetZoneId);

    const wsResolved = plantilla.lineItems.filter(
      li => resolveCost(li, pricebookByHint, laborOverhead, tradeId, category.id, businessState).source !== 'template-default'
        || (li.unitPriceUsd > 0 && !li.pricebookItemHint)
    ).length;
    const newUnpriced = items.filter(isUnpricedItem).length;
    const wsNote = wsResolved > 0 ? ` (${wsResolved}${t('estimado.plantillas.toast.deconfiguracion')}` : '';
    const zoneNote = targetZoneId
      ? ` → ${zones.find(z => z.id === targetZoneId)?.name ?? t('estimado.plantillas.zonaGenerica')}`
      : ` ${t('estimado.plantillas.toast.zonanueva')}${plantilla.name}"`;

    if (newUnpriced > 0) {
      toast(
        `Added "${plantilla.name}" — when you're ready, fill in the yellow costs on the left.`,
        { duration: 5000 }
      );
      onFocusUnpriced?.();
    } else {
      toast.success(`Added "${plantilla.name}" — ${items.length} line${items.length === 1 ? '' : 's'}${wsNote}${zoneNote}`);
    }
  };

  const handleAdd = (plantilla: EstimateTemplate) => {
    if (unpricedExisting > 0) {
      setPendingTemplate(plantilla);
      return;
    }
    executeAdd(plantilla);
  };

  return (
    <>
    <div className="p-4 space-y-2 pb-6">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="label-ui">{category.label}</h3>
        <span className="label-ui">
          <span className="font-data">{templates.length}</span>{t('estimado.plantillas.conteo.servicios')}
        </span>
      </div>

      {unpricedExisting > 0 && (
        <PricingNextStepCard
          variant="sidebar"
          unpricedCount={unpricedExisting}
          nextItemLabel={nextUnpriced?.description}
          onShowNext={() => onFocusUnpriced?.()}
        />
      )}

      {templates.map(plantilla => {
        const expanded = expandedId === plantilla.id;
        const conf = CONFIDENCE_PILL[plantilla.confidence];
        const lineSum = plantilla.lineItems.reduce((s, li) => s + li.qty * li.unitPriceUsd, 0);
        const sumDiffersFromMedian = Math.abs(lineSum - plantilla.defaultTotalUsdMedian) > 1;
        const warnings = getTemplateWarnings(plantilla);
        const warnLevel = highestSeverity(warnings);
        const calcResolves = !!(plantilla.hasCalculatorVariant && plantilla.calculatorTemplateId && calculatorIds.has(plantilla.calculatorTemplateId));

        return (
          <div
            key={plantilla.id}
            className="bg-white border border-acero-200 rounded-2xl overflow-hidden transition-[border-color] duration-150 hover:border-acero-300"
          >
            {/* Card head — compact: name, price, one-tap add */}
            <div className="p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-navy-950 text-sm leading-snug block">{plantilla.name}</span>
                  {/* La banda de precio solo sale si la plantilla trae banda. Hoy
                      ninguna la trae —las 979 tienen low/high/median en 0— así que
                      esto pintaba "$0–$0 · med $0" en cada tarjeta, y era el primer
                      precio que veía un contratista al abrir la app. Sin banda se
                      enseña de qué se compone la plantilla, que sí es un dato real. */}
                  <div className="text-[11px] text-acero-500 mt-0.5 font-data tabular-nums">
                    {plantilla.defaultTotalUsdLow > 0 || plantilla.defaultTotalUsdHigh > 0 || plantilla.defaultTotalUsdMedian > 0 ? (
                      <>
                        ${plantilla.defaultTotalUsdLow.toLocaleString()}–${plantilla.defaultTotalUsdHigh.toLocaleString()}
                        <span className="text-acero-300 mx-1">·</span>
                        med ${plantilla.defaultTotalUsdMedian.toLocaleString()}
                      </>
                    ) : (
                      t('estimado.plantillas.conteoPartidas', { count: plantilla.lineItems.length })
                    )}
                  </div>
                </div>
                <Button variant="secondary" size="sm" className="shrink-0" onClick={() => handleAdd(plantilla)} leftIcon={<Plus size={13} />}>{t('estimado.plantillas.boton.agregar4')}                </Button>
              </div>

              {/* Secondary row: zone + details — only when expanded or on demand */}
              <div className="flex items-center gap-2 mt-2">
                <select
                  value={zoneByTemplateId[plantilla.id] ?? ''}
                  onChange={(e) => setZoneByTemplateId(prev => ({ ...prev, [plantilla.id]: e.target.value }))}
                  title={t('estimado.plantillas.zona.title')}
                  aria-label={t('estimado.plantillas.zona.arialabel')}
                  className="flex-1 min-w-0 h-8 px-2 bg-white border border-acero-300 text-acero-700 text-[11px] rounded-lg truncate transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                >
                  <option value="">{t('estimado.plantillas.zona.auto')}</option>
                  {zones.map(z => (
                    <option key={z.id} value={z.id}>{t('estimado.plantillas.zona.prefijo')}{z.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => setExpandedId(expanded ? null : plantilla.id)}
                  className="h-8 px-2.5 bg-white border border-acero-300 text-acero-600 text-[11px] font-medium rounded-lg hover:border-acero-400 hover:text-navy-950 transition-colors duration-150 flex items-center gap-1 shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                >
                  {expanded ? <><ChevronUp size={12} />{t('estimado.plantillas.boton.menos')}</> : <><ChevronDown size={12} /> Info</>}
                </button>
              </div>
            </div>

            {/* Expanded panel — badges, description, line items */}
            {expanded && (
              <div className="px-3 pb-3 pt-0 border-plantilla border-acero-100 bg-acero-50 animate-in slide-in-from-top-2">
                <div className="flex items-center gap-x-3 gap-y-2 flex-wrap pt-3 mb-2">
                  {plantilla.licensingFlag.some(f => f !== 'none') && (
                    <StatusDot tone="aviso">
                      <span className="inline-flex items-center gap-1 text-xs"><ShieldAlert size={11} /> {plantilla.licensingFlag.filter(f => f !== 'none').join(', ')}</span>
                    </StatusDot>
                  )}
                  {plantilla.subcontractedCommonly && (
                    <MetaTag icon={<Users size={10} />}>{t('estimado.plantillas.meta.subcontrata')}</MetaTag>
                  )}
                  {plantilla.hasCalculatorVariant && calcResolves && (
                    <MetaTag icon={<Calculator size={10} />}>{t('estimado.plantillas.meta.calculadora')}</MetaTag>
                  )}
                  {plantilla.hasCalculatorVariant && !calcResolves && (
                    <MetaTag icon={<Construction size={10} />}>{t('estimado.plantillas.meta.calculadorapronto')}</MetaTag>
                  )}
                  {conf && conf.label !== 'estimado.confianza.sourced' && (
                    <StatusDot tone={conf.tone}><span className="text-xs">{t(conf.label)}</span></StatusDot>
                  )}
                  {warnLevel === 'error' && (
                    <StatusDot tone="peligro">
                      <span className="inline-flex items-center gap-1 text-xs"><AlertTriangle size={11} /> {t('estimado.plantillas.aviso.datos')}</span>
                    </StatusDot>
                  )}
                  {warnLevel === 'warn' && (
                    <StatusDot tone="aviso">
                      <span className="inline-flex items-center gap-1 text-xs"><AlertTriangle size={11} /> {t('estimado.plantillas.aviso.precios')}</span>
                    </StatusDot>
                  )}
                </div>
                <p className="text-[11px] text-acero-600 leading-snug mb-3">{plantilla.description}</p>
                {/* Data review warnings (admin-visible) */}
                {warnings.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {warnings.map(w => <WarningCard key={w.ruleId} w={w} />)}
                  </div>
                )}

                {/* Line items preview */}
                <h5 className="label-ui mt-3 mb-2">{t('estimado.plantillas.partidas.titulo')}<span className="font-data">{plantilla.lineItems.length}</span>)</h5>
                <ul className="text-[11px] divide-y divide-acero-100 bg-white border border-acero-200 rounded-xl overflow-hidden">
                  {plantilla.lineItems.map((li, i) => (
                    <li key={i} className="px-3 py-2 flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-navy-950">{li.description}</span>
                        <div className="text-[11px] text-acero-500 mt-0.5 flex items-center gap-1.5">
                          {/* Sin importes: el unitPriceUsd es 0 en las 6.604 partidas
                              del corpus, así que "× $0.00" y un total de "$0.00" era
                              todo lo que se enseñaba. La mano de obra sí trae un dato
                              real —sus horas-hombre— y el resto, su medida. */}
                          <span className="font-data tabular-nums">
                            {li.type === 'labor' && li.manHours
                              ? t('estimado.plantillas.horasHombre', { count: li.manHours })
                              : `${li.qty} ${li.unit}`}
                          </span>
                          <span className="label-ui">{nombreDeDato(li.type, t)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                {lineSum > 0 && (
                  <div className="text-[11px] flex justify-between mt-2 px-1">
                    <span className="text-acero-500">{t('estimado.plantillas.partidas.suma')}</span>
                    <span className="font-data tabular-nums font-medium text-navy-950">${lineSum.toFixed(2)}</span>
                  </div>
                )}
                {lineSum > 0 && sumDiffersFromMedian && (
                  <p className="text-[11px] text-aviso mt-1 px-1 leading-snug">
                    {t('estimado.plantillas.avisoMediana')}<span className="font-data tabular-nums">${lineSum.toFixed(2)}</span>{t('estimado.plantillas.aviso.mediana2')}<span className="font-data tabular-nums">${plantilla.defaultTotalUsdMedian.toLocaleString()}</span>{t('estimado.plantillas.avisoMediana.cierre')}
                  </p>
                )}

                {/* Assumptions / Exclusions */}
                <h5 className="label-ui mt-4 mb-1.5">{t('estimado.plantillas.seccion.supuestos')}</h5>
                <p className="text-[11px] text-acero-600 leading-relaxed">{plantilla.defaultAssumptions}</p>
                <h5 className="label-ui mt-3 mb-1.5">{t('estimado.plantillas.seccion.exclusiones')}</h5>
                <p className="text-[11px] text-acero-600 leading-relaxed">{plantilla.defaultExclusions}</p>

                {/* Regulatory */}
                {(plantilla.applicableAnsiStandards.length + plantilla.applicableOshaStandards.length + plantilla.stateSpecificRegs.length + plantilla.permitTriggers.length) > 0 && (
                  <>
                    <h5 className="label-ui mt-4 mb-1.5">{t('estimado.plantillas.seccion.normativa')}</h5>
                    <div className="flex flex-wrap gap-1">
                      {plantilla.applicableAnsiStandards.map(s => (
                        <span key={s} className="text-[11px] font-data bg-acero-100 text-acero-700 px-1.5 py-0.5 rounded-lg">ANSI {s}</span>
                      ))}
                      {plantilla.applicableOshaStandards.map(s => (
                        <span key={s} className="text-[11px] font-data bg-acero-100 text-acero-700 px-1.5 py-0.5 rounded-lg">OSHA {s}</span>
                      ))}
                      {plantilla.stateSpecificRegs.map(s => (
                        <span key={s} className="text-[11px] font-data bg-acero-100 text-acero-700 px-1.5 py-0.5 rounded-lg">{t('estimado.plantillas.etiqueta.estado')} {s}</span>
                      ))}
                      {plantilla.permitTriggers.map(s => (
                        <span key={s} className="text-[11px] font-data bg-acero-100 text-acero-700 px-1.5 py-0.5 rounded-lg">{t('estimado.plantillas.etiqueta.permiso')} {s}</span>
                      ))}
                    </div>
                  </>
                )}

                {/* Sources */}
                {plantilla.sourceUrls.length > 0 && (
                  <>
                    <h5 className="label-ui mt-4 mb-1.5">{t('estimado.plantillas.seccion.fuentes')}</h5>
                    <ul className="text-[11px] space-y-0.5">
                      {plantilla.sourceUrls.map(u => (
                        <li key={u}>
                          <a
                            href={u}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-navy-700 hover:text-navy-950 hover:underline underline-offset-4 inline-flex items-center gap-1 truncate max-w-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 rounded"
                          >
                            <ExternalLink size={11} /> {u}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>

    <PricingNextStepModal
      open={!!pendingTemplate}
      unpricedCount={unpricedExisting}
      nextItemLabel={nextUnpriced?.description}
      pendingLabel={pendingTemplate?.name}
      onShowNext={() => {
        setPendingTemplate(null);
        onFocusUnpriced?.();
      }}
      onContinueAnyway={() => {
        if (pendingTemplate) executeAdd(pendingTemplate);
        setPendingTemplate(null);
      }}
      onClose={() => setPendingTemplate(null)}
    />
    </>
  );
};

/** Renders a single warning row inside the expanded card. Color-coded by
 *  severity so admins can spot data issues at a glance. */
const WarningCard: React.FC<{ w: TemplateWarning }> = ({ w }) => {
  const palette =
    w.severity === 'error' ? { bg: 'bg-peligro-bg', border: 'border-peligro/25', fg: 'text-peligro', icon: <AlertTriangle size={13} /> }
    : w.severity === 'warn' ? { bg: 'bg-aviso-bg',  border: 'border-aviso/25',   fg: 'text-aviso',   icon: <AlertTriangle size={13} /> }
    :                         { bg: 'bg-acero-50',  border: 'border-acero-200',  fg: 'text-acero-600', icon: <Info size={13} /> };
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-xl border ${palette.bg} ${palette.border}`}>
      <span className={`${palette.fg} shrink-0 mt-0.5`}>{palette.icon}</span>
      <div className="text-[11px] leading-snug">
        <p className={`font-medium ${palette.fg}`}>{w.message}</p>
        <p className="text-[11px] mt-0.5 text-acero-500 font-data">{w.ruleId}</p>
      </div>
    </div>
  );
};

export default CategoryTemplatesView;
